import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart3,
  CalendarClock,
  ClipboardList,
  FastForward,
  FileText,
  Music,
  Phone,
  PhoneCall,
  ScrollText,
  Settings,
  Sparkles,
  UserPlus,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/lib/permissions';
import { ContactDetailsModal } from './ContactDetailsModal';
import { TranscriptModal } from './TranscriptModal';
import { AnsweredByBadge, dispositionStyle, formatDisposition } from './disposition';
import { formatDuration, formatEventTime } from './format';
import bahaaImg from '@/assets/bahaa.png';
import { PoolNumbersTab } from './PoolNumbersTab';
import {
  AdminRequestError,
  fetchPoolEvents,
  fetchPoolAvailability,
  fetchPoolOutcomes,
  fetchPoolRecordings,
  fetchRecordingStatuses,
  fetchDispositionStatuses,
  fetchPoolDispositions,
  fetchTranscript,
  recordingUrl,
  linkSdr,
  unlinkSdr,
  linkSdrByEmail,
  unlinkSdrByEmail,
  releaseRungs,
  type AdminPhoneNumber,
  type AdminPool,
  type AdminSdr,
  type BoardData,
  type PoolConfigPatch,
  type PoolEvent,
  type PoolEventsCursor,
  type PoolAvailability,
  type PoolAvailabilityBand,
  type PoolAvailabilityBlocker,
  type PoolAvailabilityPolicy,
  type PoolAvailabilityReturn,
  type PoolReleasePreview,
  type PoolRingDepth,
  type PoolOutcomes,
  type PoolRecording,
  type PoolRecordingsCursor,
  type RecordingStatus,
  type DispositionStatus,
  type PoolDisposition,
  type Transcript,
} from './api';
import { buildSdrRoster, formatSpend, type SdrRosterEntry } from './board-state';

// HH:MM:SS (from the core) -> HH:MM (what <input type="time"> wants).
function toTimeInput(t: string | null): string {
  return t ? t.slice(0, 5) : '';
}

// The pool dialing-config editor. The first three dimensions are real pool
// fields and are editable; the last two (per-caller-ID caps, DNC) are NOT
// pool-scoped — caps are age-based per phone number, DNC is per contact — so
// they're shown read-only here for the full picture, with a note on where they
// actually live.
function PoolSettingsTab({
  pool,
  callerNumbers,
  onSave,
}: {
  pool: AdminPool;
  callerNumbers: AdminPhoneNumber[];
  // Resolves to an error message to render inline, or null on success.
  onSave: (patch: PoolConfigPatch) => Promise<string | null>;
}) {
  const [retryDial, setRetryDial] = useState(String(pool.retry_dial_seconds));
  const [callsPerSdr, setCallsPerSdr] = useState(String(pool.calls_per_sdr));
  // Show the pool's stored redirect, or blank when it has none — there is no
  // default, so a blank field means the double-pickup sibling is hung up.
  const [redirectNumber, setRedirectNumber] = useState(pool.redirect_number ?? '');
  const [callableStart, setCallableStart] = useState(toTimeInput(pool.callable_start));
  const [callableEnd, setCallableEnd] = useState(toTimeInput(pool.callable_end));
  const [sequential, setSequential] = useState(pool.sequential);
  const [err, setErr] = useState<string | null>(null);
  const canAdmin = useHasPermission('console:admin');

  // Worked example for the "How caller IDs are chosen" help: use this pool's own
  // two (or more) linked numbers when it has them, otherwise a generic pair so
  // the explanation still reads concretely for a pool with 0–1 caller IDs.
  const [firstNum, secondNum] = callerNumbers;
  const [exampleA, exampleB] =
    firstNum && secondNum
      ? [firstNum.number, secondNum.number]
      : ['+447427905872', '+447383146018'];
  // Real per-turn count of the first example number when the pool has its own, so
  // the example reads "20 calls" concretely instead of an abstract "its cap".
  const exampleCap = firstNum ? firstNum.turn_cap : 20;

  const [saving, setSaving] = useState(false);

  async function submit() {
    const patch: PoolConfigPatch = {};

    patch.sequential = sequential;

    const retry = Number(retryDial);
    if (!Number.isInteger(retry) || retry < 1)
      return setErr('Retry interval must be a positive whole number of seconds.');
    patch.retry_dial_seconds = retry;

    const perSdr = Number(callsPerSdr);
    if (!Number.isInteger(perSdr) || perSdr < 1 || perSdr > 10)
      return setErr('Calls per SDR must be a whole number from 1 to 10.');
    patch.calls_per_sdr = perSdr;

    // Double-pickup redirect: blank clears it (sent as '' → core stores null);
    // otherwise require E.164 (+ then 7–15 digits), matching the core's check.
    const redirect = redirectNumber.trim();
    if (redirect !== '' && !/^\+[1-9]\d{6,14}$/.test(redirect))
      return setErr('Redirect number must be E.164, e.g. +447700900000 (or blank to disable).');
    patch.redirect_number = redirect;

    // Callable window: send only when both bounds are set (the core validates
    // that one isn't set without the other).
    if (callableStart && callableEnd) {
      patch.callable_start = callableStart;
      patch.callable_end = callableEnd;
    } else if (callableStart || callableEnd) {
      return setErr('Set both callable start and end, or leave both blank.');
    }

    // The save failing is about the form the operator is looking at, and the board's
    // error banner is behind this panel — so the message has to land here.
    setErr(null);
    setSaving(true);
    setErr(await onSave(patch));
    setSaving(false);
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto">
      {err && <p className="text-destructive text-xs">⚠ {err}</p>}
      {!canAdmin && (
        <p className="text-muted-foreground text-xs">
          Changing how a pool dials is an admin action — these are shown as they are set.
        </p>
      )}

      {/* --- Editable: real pool fields --- */}
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={sequential}
          disabled={!canAdmin}
          onChange={(e) => setSequential(e.target.checked)}
          className="mt-0.5 disabled:opacity-60"
        />
        <span className="flex flex-col gap-0.5">
          <span className="font-medium">One pass through the list (sequential)</span>
          <span className="text-muted-foreground text-xs">
            Dial each contact once, in list order (top to bottom), then stop. No re-dialing. Leave
            off for continuous looping dialing.
          </span>
        </span>
      </label>

      {/* Max attempts per contact and Retry interval are hidden from the UI for
            now (the state + submit logic stay so each pool's stored values are
            preserved on save). Un-hide by restoring these two <label> blocks. */}

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Calls per SDR</span>
        <input
          type="number"
          min={1}
          max={10}
          value={callsPerSdr}
          disabled={!canAdmin}
          onChange={(e) => setCallsPerSdr(e.target.value)}
          className="rounded border px-2 py-1 text-sm disabled:opacity-60"
        />
        <span className="text-muted-foreground text-xs">
          How many calls each SDR dials in parallel (1&ndash;10). The first call a person answers
          connects to the SDR. If another also answers (double-pickup), that call is sent to the
          redirect number below. 1 = one call at a time. New pools default to 2.
        </span>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Redirect number (double-pickup)</span>
        <div className="flex items-center gap-2">
          <img
            src={bahaaImg}
            alt="Bahaa"
            title="Bahaa"
            className="h-10 w-10 shrink-0 rounded-full border object-cover"
          />
          <input
            type="tel"
            value={redirectNumber}
            placeholder="+447700900000 (blank = hang up)"
            disabled={!canAdmin}
            onChange={(e) => setRedirectNumber(e.target.value)}
            className="flex-1 rounded border px-2 py-1 text-sm disabled:opacity-60"
          />
        </div>
        <span className="text-muted-foreground text-xs">
          When two contacts answer the same SDR&apos;s parallel calls at once, the SDR takes one and
          the other is transferred to this number (E.164) instead of being hung up. Blank disables
          the transfer (the extra call is hung up).
        </span>
      </label>

      {/* Callable hours is hidden from the UI for now (the callableStart/End
            state + submit logic stay, so each pool's stored window is preserved
            on save). Un-hide by restoring the <fieldset> here.

            The read-only "Other limits (not pool settings)" section — per-caller-ID
            outbound caps and do-not-call — is also hidden for now. */}

      {/* Read-only explainer: caller IDs are NOT round-robin. People assume
            even rotation; this spells out the real best-match-then-cap behavior
            with a worked example built from this pool's own numbers. */}
      <details className="border-t pt-3 text-sm">
        <summary className="cursor-pointer font-medium select-none">
          How caller IDs are chosen
        </summary>
        <div className="text-muted-foreground mt-2 flex flex-col gap-2 text-xs">
          <p>
            A pool dials its caller IDs in a <b className="text-foreground">fixed order</b>. It uses
            the first number for a batch of calls, then rotates to the next, and so on — looping
            back to the first, continuously.
          </p>
          <p>
            Each number’s cap is <b className="text-foreground">how many calls it makes per turn</b>{' '}
            before rotating. The pool loops through the numbers{' '}
            <b className="text-foreground">continuously</b> — there is no daily limit; it just keeps
            rotating.
          </p>
          <p className="bg-muted/50 rounded p-2">
            <b className="text-foreground">Example.</b> This pool dials from{' '}
            <span className="font-mono">{exampleA}</span> then{' '}
            <span className="font-mono">{exampleB}</span>. It sends {exampleCap} calls from{' '}
            <span className="font-mono">{exampleA}</span>, then switches to{' '}
            <span className="font-mono">{exampleB}</span> for its turn, then back to{' '}
            <span className="font-mono">{exampleA}</span> — over and over, continuously.
          </p>
        </div>
      </details>

      {canAdmin && (
        <div className="flex justify-end border-t pt-3">
          <Button size="sm" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}

// Tailwind classes per event_type for the timeline chip — grouped by meaning so
// the eye can scan: green = connected/progress, amber = unconnected outcome,
// red = voicemail/failure, blue = SDR presence, gray = pacing/lifecycle.
// The high-volume "why nothing is dialing right now" events: nothing dialable
// + the re-poll it schedules. They repeat every retry tick while a contact is in
// its redial gap, so the timeline can hide them to focus on call/SDR activity.
const PACING_EVENTS = new Set(['no_contacts', 'retry_scheduled']);

const EVENT_STYLE: Record<string, string> = {
  dial_attempted: 'bg-sky-100 text-sky-800',
  ringing: 'bg-sky-100 text-sky-800',
  answered: 'bg-emerald-100 text-emerald-800',
  // The true "a real person is on the line" moment (AMD-confirmed human) — given
  // its own teal so it stands out from the Twilio-level `answered`.
  human_speaking: 'bg-teal-100 text-teal-800',
  ended: 'bg-emerald-100 text-emerald-800',
  no_answer: 'bg-amber-100 text-amber-800',
  busy: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  voicemail_detected: 'bg-red-100 text-red-800',
  // SDR dropped a call AMD bridged as a human but was really a voicemail (ground-
  // truth AMD miss) — same red as the auto-detected voicemail, since both are
  // "machine". `manual_voicemailed` is the legacy name, kept so pre-rename rows in
  // the timeline still render red rather than falling through to the default.
  manual_dropped: 'bg-red-100 text-red-800',
  manual_voicemailed: 'bg-red-100 text-red-800',
  // AMD couldn't classify (noisy line) — call kept; amber = uncertain outcome.
  amd_unknown: 'bg-amber-100 text-amber-800',
  // The call-lifetime/pre-answer watchdog force-terminated a call whose terminal
  // Twilio callback never arrived — red, since the call didn't complete normally.
  call_timeout: 'bg-red-100 text-red-800',
  sdr_joined: 'bg-indigo-100 text-indigo-800',
  sdr_left: 'bg-indigo-100 text-indigo-800',
  // The system forced the SDR out of a finished/closed pool (not a voluntary
  // leave) — rose so a forced eviction stands apart from the indigo SDR-presence
  // events.
  sdr_pushed_out: 'bg-rose-100 text-rose-800',
  // The system PURGED an SDR whose leave drain stalled (a call leg that never
  // ended). Deeper rose than sdr_pushed_out — this is a fault signal, not a clean
  // leave, so it should catch the eye when scanning the timeline.
  sdr_purged: 'bg-rose-200 text-rose-900',
  // Graceful exit in progress — draining the SDR's live call before removing them.
  sdr_leaving: 'bg-indigo-100 text-indigo-800',
  sdr_idle: 'bg-gray-100 text-gray-700',
  sdr_on_call: 'bg-indigo-100 text-indigo-800',
  sdr_wrap_up: 'bg-gray-100 text-gray-700',
  no_contacts: 'bg-gray-100 text-gray-700',
  retry_scheduled: 'bg-gray-100 text-gray-700',
  pool_started: 'bg-gray-100 text-gray-700',
  pool_ended: 'bg-violet-100 text-violet-800',
  pool_idle_shutdown: 'bg-gray-100 text-gray-700',
  // Pause/resume lifecycle — amber for "stalled, can't dial right now", green for
  // "dialing again", so a paused stretch is easy to spot in the timeline.
  pool_paused_all_sdrs_on_call: 'bg-amber-100 text-amber-800',
  pool_paused_no_sdrs: 'bg-amber-100 text-amber-800',
  pool_paused_off_hours: 'bg-amber-100 text-amber-800',
  pool_resumed: 'bg-emerald-100 text-emerald-800',
  // Deploy drain + operator pause/resume — same amber/green language.
  pool_draining: 'bg-amber-100 text-amber-800',
  pool_paused_operator: 'bg-amber-100 text-amber-800',
  pool_resumed_dialing: 'bg-emerald-100 text-emerald-800',
  pool_resumed_operator: 'bg-emerald-100 text-emerald-800',
};

// "Assign SDRs" — pick which people are assigned to this pool from the AUTH
// DIRECTORY (the real @throxy.com sign-ins), so an assignment always lines up with
// who can actually log in. An SDR sees a pool in their cockpit "Choose a pool" ONLY
// if they're assigned here (strict). Assignment is keyed by email (the core
// get-or-creates the SDR row); the dev fallback (auth off, no email) keys by the
// core SDR id. `onChanged` re-fetches the board so the ticks + card count reflect it.
function PoolSdrsTab({
  pool,
  roster,
  live,
  onChanged,
}: {
  pool: AdminPool;
  roster: SdrRosterEntry[];
  live: Set<number>;
  onChanged: () => void;
}) {
  const [query, setQuery] = useState('');
  const canAssign = useHasPermission('sdr-assignment:write');
  // The roster key currently being (un)assigned — disables just that row.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Last successful (un)assignment — the modal stays open across several ticks,
  // so confirm each one inline rather than only via the board reload.
  const [ok, setOk] = useState<{ name: string; assign: boolean } | null>(null);
  // Optimistic tick state, keyed by roster key. The server round-trip → board
  // reload → roster prop takes a couple of seconds, so without this the checkbox
  // lags well behind the confirmation. We flip it immediately and let the effect
  // below drop each override once the reloaded roster agrees (or on failure).
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setOptimistic((cur) => {
      const next: Record<string, boolean> = {};
      for (const [key, val] of Object.entries(cur)) {
        const entry = roster.find((r) => r.key === key);
        // Keep the override only while it still disagrees with server truth.
        if (entry && entry.pool_ids.includes(pool.id) !== val) next[key] = val;
      }
      return Object.keys(next).length === Object.keys(cur).length ? cur : next;
    });
  }, [roster, pool.id]);

  const q = query.trim().toLowerCase();
  const matches = (e: SdrRosterEntry) =>
    q ? `${e.name} ${e.email ?? ''}`.toLowerCase().includes(q) : true;

  const isAssigned = (e: SdrRosterEntry) => optimistic[e.key] ?? e.pool_ids.includes(pool.id);
  const byName = (a: SdrRosterEntry, b: SdrRosterEntry) => a.name.localeCompare(b.name);
  const shown = roster.filter(matches);
  const assigned = shown.filter(isAssigned).sort(byName);
  const available = shown.filter((e) => !isAssigned(e)).sort(byName);

  const toggle = async (e: SdrRosterEntry, assign: boolean) => {
    setBusyKey(e.key);
    setErr(null);
    setOk(null);
    setOptimistic((cur) => ({ ...cur, [e.key]: assign })); // flip the tick now
    try {
      // Prefer email (the directory identity); fall back to the core SDR id for a
      // dev-only row that has no email (impersonation).
      if (e.email) {
        await (assign ? linkSdrByEmail(pool.id, e.email) : unlinkSdrByEmail(pool.id, e.email));
      } else if (e.sdrId != null) {
        await (assign ? linkSdr(e.sdrId, pool.id) : unlinkSdr(e.sdrId, pool.id));
      }
      setOk({ name: e.name || e.email || 'SDR', assign });
      onChanged();
    } catch (ex) {
      setErr(ex instanceof AdminRequestError ? ex.message : String(ex));
      // Roll the tick back — the change didn't land.
      setOptimistic((cur) => {
        const { [e.key]: _dropped, ...rest } = cur;
        return rest;
      });
    } finally {
      setBusyKey(null);
    }
  };

  const row = (e: SdrRosterEntry, assignedRow: boolean) => (
    <li key={e.key} className="hover:bg-muted/50 flex items-center gap-2 rounded px-1 py-1">
      <input
        type="checkbox"
        checked={assignedRow}
        disabled={busyKey === e.key || !canAssign}
        title={assignedRow ? 'Unassign from this pool' : 'Assign to this pool'}
        onChange={(ev) => void toggle(e, ev.target.checked)}
      />
      <span className="mr-auto min-w-0 truncate text-sm">
        {e.name || '(unnamed)'}
        {e.sdrId != null && live.has(e.sdrId) && (
          <span className="ml-1 text-xs font-bold text-emerald-700">● calling</span>
        )}
        {e.email && <span className="text-muted-foreground ml-1 text-xs">· {e.email}</span>}
      </span>
      {busyKey === e.key && <span className="text-muted-foreground text-xs">…</span>}
    </li>
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <p className="text-muted-foreground text-xs">
        These are the people signed in with their @throxy.com Google account. An SDR sees this pool
        in their cockpit “Choose a pool” only if they’re assigned here — tick to assign, untick to
        remove. Don’t see someone? They need to sign in to the dialer once first.
      </p>

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search people…"
        className="border-input rounded border px-2 py-1 text-sm"
      />

      {err && <p className="text-destructive text-xs">⚠ {err}</p>}
      {!err && ok && (
        <p
          className={cn('text-xs font-medium', ok.assign ? 'text-emerald-700' : 'text-destructive')}
        >
          {ok.assign ? `✓ ${ok.name} assigned` : `✕ ${ok.name} unassigned`}
        </p>
      )}

      <div className="flex-1 overflow-y-auto">
        {roster.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm italic">
            No one has signed in to the dialer yet.
          </p>
        ) : (
          <>
            <div className="text-muted-foreground mb-1 text-[0.68rem] font-bold uppercase">
              Assigned to this pool
            </div>
            {assigned.length === 0 ? (
              <p className="text-muted-foreground py-2 text-center text-xs italic">
                {q ? `No matches for “${query}”.` : 'No one assigned yet.'}
              </p>
            ) : (
              <ul className="space-y-1">{assigned.map((e) => row(e, true))}</ul>
            )}

            {available.length > 0 && (
              <>
                <div className="text-muted-foreground mt-3 mb-1 text-[0.68rem] font-bold uppercase">
                  Available
                </div>
                <ul className="space-y-1">{available.map((e) => row(e, false))}</ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function OutcomeRow({
  label,
  count,
  pct,
  color,
  title,
}: {
  label: string;
  count: number;
  pct: number;
  color: string;
  title?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium" title={title}>
          {label}
        </span>
        <span className="text-muted-foreground">
          <span className="text-foreground font-bold">{count}</span> · {pct}%
        </span>
      </div>
      <div className="bg-muted h-2 w-full overflow-hidden rounded">
        <div className="h-full rounded" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// Why a pool is quiet, and when it stops being quiet. "Available now" and "awaiting a
// redial pass" stay two numbers: summing them would show a healthy pool at the exact
// moment the automation has stopped.
const BAND_LABELS: Record<PoolAvailabilityBand['band'], string> = {
  now: 'Ready now',
  within_1h: 'Within 1 hour',
  within_3h: 'Within 3 hours',
  within_1d: 'Within a day',
  within_1w: 'Within a week',
  beyond_1w: 'Over a week away',
  unschedulable: 'No callable window',
};

const BLOCKER_LABELS: Record<PoolAvailabilityBlocker, string> = {
  off_hours: 'waiting for their calling window',
  lead_cooldown: 'waiting on the ring ladder',
  pool_cooldown: 'rung by this pool recently',
  provider_refused: 'the carrier refuses these numbers',
  unknown_timezone: 'we can’t resolve a calling window for them',
  claimed_pending_redial: 'awaiting a redial pass',
  none: '',
};

// The `?` marks. A definition an operator needs twice a month should not be four
// paragraphs of prose the other twenty-eight days.
function Hint({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger
        aria-label={title}
        className="text-muted-foreground hover:bg-muted-foreground hover:text-background focus-visible:ring-ring inline-flex size-3.5 flex-none items-center justify-center rounded-full border text-[9px] leading-none font-semibold focus-visible:ring-2 focus-visible:outline-none"
      >
        ?
      </PopoverTrigger>
      <PopoverContent>
        <p className="mb-1 font-semibold">{title}</p>
        <p className="text-muted-foreground">{children}</p>
      </PopoverContent>
    </Popover>
  );
}

// A fold, shut. Used twice, for the two things worth having and not worth reading every
// time the panel opens.
function Fold({
  summary,
  defaultOpen = false,
  className,
  children,
}: {
  summary: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <details open={defaultOpen} className={className}>
      <summary className="text-muted-foreground hover:text-foreground marker:content-[''] flex cursor-pointer list-none items-center gap-1.5 text-xs select-none">
        <span className="border-y-[3.5px] border-l-4 border-y-transparent border-l-current transition-transform in-open:rotate-90" />
        {summary}
      </summary>
      <div className="pt-1.5">{children}</div>
    </details>
  );
}

// One colour per thing that holds a lead, so a row's cause is legible before its words
// are read. Kept in sync with TIMELINE_META below, which says the same thing in words —
// colour alone is not a label.
const RETURN_COLORS: Record<PoolAvailabilityBlocker, string> = {
  none: 'var(--color-emerald-600)',
  claimed_pending_redial: 'var(--color-amber-600)',
  off_hours: 'var(--color-pink-700)',
  lead_cooldown: 'var(--color-blue-600)',
  pool_cooldown: 'var(--color-blue-600)',
  provider_refused: 'var(--color-neutral-400)',
  unknown_timezone: 'var(--color-neutral-400)',
};

const TIMELINE_META: Record<PoolAvailabilityBlocker, string> = {
  none: 'ready to dial',
  claimed_pending_redial: 'rejoin when those run out',
  off_hours: '',
  lead_cooldown: 'off the ring ladder',
  pool_cooldown: 'rung by this pool recently',
  provider_refused: 'carrier refused, retries itself',
  unknown_timezone: 'no calling window resolves for these',
};

// The closed set `undialable_counts/1` emits, in words. Anything new falls back to the key.
const NEVER_LABELS: Record<string, string> = {
  dnc: 'Do Not Call',
  deleted: 'Deleted',
  barred_country: 'Barred country',
  lead_ceiling: 'At the lifetime ring cap',
};

const clock = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

// Calendar days apart, not hours apart — 23:30 and 00:30 are one hour and one day.
function daysAhead(at: Date, now: Date): number {
  const a = new Date(at.getFullYear(), at.getMonth(), at.getDate());
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a.getTime() - b.getTime()) / 86_400_000);
}

const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

// A time that is not today has to say so. An 09:00 tomorrow and an 09:00 today are the
// same six characters otherwise, and the list is read as an ordering.
function whenLabel(at: Date, now: Date, prefix: string): string {
  // A span's edge is now-plus-a-band to the second, so "by 15:17" claims a precision the
  // band does not have. Rounded up to the quarter hour: still an upper bound, and readable.
  if (prefix) {
    const q = 15 * 60_000;
    at = new Date(Math.ceil(at.getTime() / q) * q);
  }
  const days = daysAhead(at, now);
  if (days <= 0) return `${prefix}${clock.format(at)}`;
  if (days === 1) return `${prefix}${clock.format(at)} tmw`;
  return `${prefix}${clock.format(at)} ${weekday.format(at)}`;
}

// When a row comes back, in the fewest words that are still true. A window is an instant
// and gets a time; a rung is a span and gets its far edge, because saying "14:43" of a
// group whose leads return one at a time is a precision that isn't there.
function returnKey(row: PoolAvailabilityReturn, now: Date): string {
  if (row.band === 'now') return row.blocker === 'none' ? 'now' : 'next';
  if (row.band === 'beyond_1w') return 'later';
  // A rolling seven days from this read, which is not the same as the calendar week.
  if (row.band === 'within_1w') return 'within 7d';
  if (!row.at) return 'unknown';

  const at = new Date(row.at);
  return whenLabel(at, now, row.span ? 'by ' : '');
}

function TimelineRow({
  row,
  max,
  now,
  autoRequeue,
}: {
  row: PoolAvailabilityReturn;
  max: number;
  now: Date;
  autoRequeue: boolean;
}) {
  const base = row.blocker === 'off_hours' ? row.zones.join(', ') : TIMELINE_META[row.blocker];
  // A window opening does not free a claimed lead by itself. While auto-redial is on a
  // pass hands the claim back without anyone doing anything, so it is only worth the words
  // when it is switched off — otherwise this row would carry a caveat that never bites.
  const held = !autoRequeue && row.claimed > 0 && row.band !== 'now';
  const meta = held ? `${base} · ${row.claimed.toLocaleString()} need a redial pass` : base;

  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)_3rem] items-center gap-2.5 sm:grid-cols-[5.5rem_minmax(0,1fr)_3rem_12rem]">
      <span className="text-xs tabular-nums">{returnKey(row, now)}</span>
      <div
        className="h-3.5 min-w-0.5 rounded-[3px]"
        style={{
          // Capped: the scale is set by the largest FUTURE row so arrivals stay legible,
          // which makes ready-now several times the cell. It is the headline anyway.
          width: `${Math.min(100, Math.max(1, (row.count / max) * 100))}%`,
          background: RETURN_COLORS[row.blocker],
        }}
      />
      <span className="text-right text-xs tabular-nums">{row.count.toLocaleString()}</span>
      <span className="text-muted-foreground hidden truncate text-[11px] sm:block">{meta}</span>
    </div>
  );
}

// The panel's spine: everything this pool can still dial, in the order it comes back.
// Grouping by cause instead breaks the time order, and the order is the question.
function AvailabilityTimeline({
  rows,
  autoRequeue,
}: {
  rows: PoolAvailabilityReturn[];
  autoRequeue: boolean;
}) {
  const now = new Date();
  // Scaled to the largest FUTURE row. Ready-now is usually the whole pool and would flatten
  // every arrival to the same two-pixel stub, which is the one comparison this list is for.
  // It is also stated at 4xl directly above, so it does not need the bar.
  const max = Math.max(...rows.filter((r) => r.band !== 'now').map((r) => r.count), 1);

  // A global pool opens a dozen zones a handful of leads at a time — nine rows of "4 · Asia"
  // under the two that matter. Timed rows below half a percent of the pool fold into one
  // line that keeps their span and their total; nothing is dropped, only de-emphasised.
  const total = rows.reduce((n, r) => n + r.count, 0);
  const tiny = (r: PoolAvailabilityReturn) =>
    r.band !== 'now' && r.at !== null && !r.span && r.count < Math.max(5, total * 0.005);
  const small = rows.filter(tiny);
  const shown = small.length >= 3 ? rows.filter((r) => !tiny(r)) : rows;
  const folded =
    small.length >= 3
      ? {
          count: small.reduce((n, r) => n + r.count, 0),
          claimed: small.reduce((n, r) => n + r.claimed, 0),
          first: new Date(small[0]!.at!),
          last: new Date(small[small.length - 1]!.at!),
          groups: small.length,
        }
      : null;
  const asleep = rows.filter((r) => r.blocker === 'off_hours').reduce((n, r) => n + r.count, 0);
  const resting = rows
    .filter((r) => r.blocker === 'lead_cooldown' || r.blocker === 'pool_cooldown')
    .reduce((n, r) => n + r.count, 0);

  return (
    <div className="flex flex-col gap-1">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-muted-foreground flex items-center gap-1.5 text-xs font-bold uppercase">
          When leads become dialable
          <Hint title="When leads become dialable">
            Every lead this pool can still call, placed at the moment it can next be rung. Read down
            the list for what happens next. A zone’s leads all wake together when its calling window
            opens; leads resting on the ring ladder come back one at a time, so those rows are a
            span rather than an instant.
          </Hint>
        </h3>
        <span className="text-muted-foreground text-[11px]">your local time</span>
      </div>

      {(asleep > 0 || resting > 0) && (
        <p className="text-muted-foreground mb-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] tabular-nums">
          {asleep > 0 && (
            <span className="flex items-center gap-1.5">
              <i className="size-2 rounded-[2px]" style={{ background: RETURN_COLORS.off_hours }} />
              <b className="text-foreground">{asleep.toLocaleString()}</b> asleep until their window
              opens
            </span>
          )}
          {resting > 0 && (
            <span className="flex items-center gap-1.5">
              <i
                className="size-2 rounded-[2px]"
                style={{ background: RETURN_COLORS.lead_cooldown }}
              />
              <b className="text-foreground">{resting.toLocaleString()}</b> resting on the ring
              ladder
            </span>
          )}
        </p>
      )}

      {shown.map((row) => (
        <TimelineRow
          key={`${row.band}:${row.blocker}:${row.at ?? 'none'}`}
          row={row}
          max={max}
          now={now}
          autoRequeue={autoRequeue}
        />
      ))}
      {folded && (
        <div className="grid grid-cols-[5.5rem_minmax(0,1fr)_3rem] items-center gap-2.5 sm:grid-cols-[5.5rem_minmax(0,1fr)_3rem_12rem]">
          <span className="text-muted-foreground text-xs tabular-nums">
            {whenLabel(folded.first, now, '')}–{whenLabel(folded.last, now, '')}
          </span>
          <div
            className="h-3.5 min-w-0.5 rounded-[3px] opacity-60"
            style={{
              width: `${Math.min(100, Math.max(1, (folded.count / max) * 100))}%`,
              background: RETURN_COLORS.off_hours,
            }}
          />
          <span className="text-right text-xs tabular-nums">{folded.count.toLocaleString()}</span>
          <span className="text-muted-foreground hidden truncate text-[11px] sm:block">
            {folded.groups} smaller zones
          </span>
        </div>
      )}
    </div>
  );
}

// A rest, in the words the ladder is configured in. `depth` is rings already taken, and a
// lead on its nth ring serves the nth rung — so this indexes from the depth, not from it
// minus one, and clamps at the end because the last rung repeats forever.
function restForDepth(depth: number, ladder: number[]): string {
  if (depth === 0) return 'never rung';
  if (ladder.length === 0) return '';

  const seconds = ladder[Math.min(depth, ladder.length) - 1];
  if (seconds === undefined) return '';
  if (seconds >= 604_800) return `rests ${Math.round(seconds / 604_800)}w`;
  if (seconds >= 86_400) return `rests ${Math.round(seconds / 86_400)}d`;
  return `rests ${Math.round(seconds / 3600)}h`;
}

// How often each lead's handset has already rung — counted across every pool, because
// that is what the ladder counts. Never shown before, and it is the difference between "a
// quiet pool" and "a pool we have already worked through".
function RingDepth({
  rows,
  rungLastHour,
  policy,
}: {
  rows: PoolRingDepth[];
  rungLastHour: number | null | undefined;
  policy: PoolAvailabilityPolicy;
}) {
  const max = Math.max(...rows.map((r) => r.leads), 1);

  return (
    <div className="flex flex-col gap-1">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-muted-foreground flex items-center gap-1.5 text-xs font-bold uppercase">
          Ring depth
          <Hint title="Ring depth">
            How often each lead’s phone has already rung, counted across every pool — that is how
            the ladder counts. Not dials by this pool alone.
          </Hint>
        </h3>
        {typeof rungLastHour === 'number' && (
          <span className="text-muted-foreground text-[11px] tabular-nums">
            {rungLastHour.toLocaleString()} rung in the last hour
          </span>
        )}
      </div>

      {rows.map((row) => (
        <div
          key={row.depth}
          className="grid grid-cols-[4rem_minmax(0,1fr)_3rem] items-center gap-2.5 sm:grid-cols-[4rem_minmax(0,1fr)_3rem_4.5rem]"
        >
          <span className="text-xs tabular-nums">
            {row.depth === policy.max_rings ? `${row.depth}+` : row.depth}
            {row.depth === 1 ? ' ring' : ' rings'}
          </span>
          <div
            className="bg-primary/70 h-4 min-w-0.5 rounded-[3px]"
            style={{ width: `${Math.max(1, (row.leads / max) * 100)}%` }}
          />
          <span className="text-right text-xs tabular-nums">{row.leads.toLocaleString()}</span>
          <span className="text-muted-foreground hidden text-right text-[11px] sm:block">
            {row.depth === policy.max_rings
              ? 'capped'
              : restForDepth(row.depth, policy.ring_ladder)}
          </span>
        </div>
      ))}
    </div>
  );
}

// The one row on this panel worth chasing, and only in one of its three states. Shut by
// default — except when it is the amber one, which puts the warning in the summary rather
// than behind a fold.
function AutoRequeueRow({
  waiting,
  readyNow,
  enabled,
  paused,
}: {
  waiting: number;
  readyNow: number;
  enabled: boolean;
  paused: boolean;
}) {
  // A paused pool holding claimed leads is doing exactly what it was told to. The core's
  // auto-redial eligibility excludes it on purpose, so calling that an automation fault
  // sends an operator looking for a break that isn't there.
  const dry = enabled && readyNow === 0 && !paused;

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-md border border-l-[3px] p-2.5 text-sm',
        dry
          ? 'border-amber-500/40 border-l-amber-500 bg-amber-500/5'
          : 'border-l-muted-foreground bg-muted',
      )}
    >
      <Fold
        defaultOpen={dry}
        className="min-w-0 flex-1"
        summary={
          <span className="text-foreground text-[13px]">
            {!enabled
              ? 'Auto-redial is off'
              : paused
                ? `Paused — ${waiting.toLocaleString()} called once are held until it resumes`
                : dry
                  ? `Pool ran dry but is still holding ${waiting.toLocaleString()} — auto-redial didn’t fire`
                  : 'Auto-redial is on'}
          </span>
        }
      >
        <p className="text-muted-foreground text-xs">
          {!enabled ? (
            <>
              The <b className="text-foreground tabular-nums">{waiting.toLocaleString()}</b> called
              once already will not come back by themselves. Not a fault, but worth knowing before
              you wonder why a pool with leads in it went quiet.
            </>
          ) : paused ? (
            <>
              The <b className="text-foreground tabular-nums">{waiting.toLocaleString()}</b> called
              once already stay held while the pool is paused — auto-redial skips a paused pool by
              design. They come back when it is resumed.
            </>
          ) : dry ? (
            <>
              Nothing is ready to dial and these are still held. The in-place pass should have run —
              look for a redial failure in this pool’s timeline, or close and reopen the pool.
            </>
          ) : (
            <>
              The <b className="text-foreground tabular-nums">{waiting.toLocaleString()}</b> rejoin
              on their own when the ready leads run out — that is auto-redial, the global setting.
              Nothing to press. This row turns amber only if the pool runs dry and still holds them.
            </>
          )}
        </p>
      </Fold>
      <span className="text-muted-foreground bg-background rounded border px-1.5 py-0.5 text-[11px]">
        {!enabled ? 'off' : paused ? 'paused' : dry ? 'check' : 'normal'}
      </span>
    </div>
  );
}

function PoolAvailabilityTab({ pool, onChanged }: { pool: AdminPool; onChanged: () => void }) {
  const [data, setData] = useState<PoolAvailability | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const canAdmin = useHasPermission('console:admin');

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    fetchPoolAvailability(pool.id)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [pool.id]);

  useEffect(() => {
    load();
  }, [load]);

  const neverReasons = data
    ? Object.entries(data.never_by_reason).filter(([, count]) => count > 0)
    : [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {err ? (
        <p className="text-destructive text-sm">{err}</p>
      ) : !data ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <div className="flex flex-col gap-5">
          <section>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="size-2.5 rounded-full bg-emerald-600" />
              <span className="text-4xl leading-none font-bold tabular-nums">
                {data.available_now.toLocaleString()}
              </span>
              <span className="text-muted-foreground text-sm">ready to dial now</span>
              <Hint title="Ready to dial now">
                Past every wait and inside its calling window. Read from Postgres — a lead on a live
                call right now still counts here.
              </Hint>
            </div>

            {data.available_on_redial > 0 && (
              <p className="text-muted-foreground mt-1.5 text-xs">
                <b className="text-foreground tabular-nums">
                  + {data.available_on_redial.toLocaleString()}
                </b>{' '}
                lined up behind them
              </p>
            )}

            <p className="text-muted-foreground mt-0.5 text-xs tabular-nums">
              of {data.total_available.toLocaleString()} dialable ·{' '}
              {(data.total_available + data.never).toLocaleString()} in the pool
            </p>
          </section>

          {data.available_on_redial > 0 && data.policy && (
            <AutoRequeueRow
              waiting={data.available_on_redial}
              readyNow={data.available_now}
              enabled={data.policy.auto_redial_enabled}
              paused={pool.operator_paused || pool.run_state === 'paused'}
            />
          )}

          {data.returns?.length ? (
            <section>
              <AvailabilityTimeline
                rows={data.returns}
                autoRequeue={data.policy?.auto_redial_enabled ?? true}
              />
            </section>
          ) : (
            // A core that has not picked up this deploy yet. The bands it does return say
            // the same thing, grouped rather than ordered.
            <div className="flex flex-col gap-1">
              <h3 className="text-muted-foreground text-xs font-bold uppercase">
                When work returns
              </h3>
              {data.bands
                .filter((b) => b.count > 0)
                .map((b) => (
                  <div key={`${b.band}:${b.blocker}`} className="flex justify-between text-sm">
                    <span>
                      {BAND_LABELS[b.band]}
                      {b.blocker !== 'none' && (
                        <span className="text-muted-foreground">
                          {' '}
                          — {BLOCKER_LABELS[b.blocker]}
                        </span>
                      )}
                    </span>
                    <span className="tabular-nums">{b.count}</span>
                  </div>
                ))}
              {data.bands.every((b) => b.count === 0) && (
                <p className="text-muted-foreground text-sm">Nothing waiting.</p>
              )}
            </div>
          )}

          {data.ring_depth && data.ring_depth.length > 0 && data.policy?.cooldown_enabled && (
            <section>
              <RingDepth
                rows={data.ring_depth}
                rungLastHour={data.rung_last_hour}
                policy={data.policy}
              />
            </section>
          )}

          {neverReasons.length > 0 && (
            <div className="flex flex-col gap-1">
              <h3 className="text-muted-foreground text-xs font-bold uppercase">
                Never dialable ({data.never.toLocaleString()})
              </h3>
              {neverReasons.map(([reason, count]) => (
                <div key={reason} className="flex justify-between text-sm">
                  <span>{NEVER_LABELS[reason] ?? reason.replace(/_/g, ' ')}</span>
                  <span className="tabular-nums">{count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}

          <p className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
            Postgres read — live calls not shown
            <Hint title="What this cannot see">
              In-flight state — a lead locked by a live call, one holding an inbound leg — is
              invisible here. This says when work returns, not why a dial did not happen a second
              ago.
            </Hint>
          </p>

          {canAdmin && (
            <RungRelease
              poolId={pool.id}
              preview={data.release ?? null}
              policy={data.policy}
              onReleased={() => {
                load();
                onChanged();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Hand leads back from the ring ladder — with the count it would move, before the press
// rather than after it.
function RungRelease({
  poolId,
  preview,
  policy,
  onReleased,
}: {
  poolId: number;
  preview: PoolReleasePreview | null;
  policy: PoolAvailabilityPolicy | undefined;
  onReleased: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  function release() {
    setBusy(true);
    setError(null);
    releaseRungs(poolId)
      .then(({ released, floor_seconds, max_rings }) => {
        setConfirming(false);
        // Nothing released is an answer rather than a failure: it says the ladder was not
        // what was holding this pool.
        setResult(
          released === 0
            ? `Nothing moved — no lead here is parked on a ladder we can pull forward. Leads past ${max_rings} rings are left where they are.`
            : `${released} lead${released === 1 ? '' : 's'} released. None returns sooner than ${describeFloor(floor_seconds)} after we last rang it, and a released lead dials once the next pass hands its claim back.`,
        );
        onReleased();
      })
      .catch((err: unknown) => {
        setError(err instanceof AdminRequestError ? err.message : 'Release failed');
      })
      .finally(() => setBusy(false));
  }

  // An older core answers without the preview. The button still works; it just cannot say
  // what it will do until it has done it.
  const movable = preview?.movable ?? null;

  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      {movable !== null && (
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-3xl leading-none font-bold text-blue-600 tabular-nums">
            {movable.toLocaleString()}
          </span>
          <span className="text-muted-foreground text-[13px]">
            {movable === 1 ? 'lead would move up' : 'leads would move up'}
          </span>
          <Hint title="Release ring ladder">
            Ends every lead’s rest early, no sooner than{' '}
            {policy ? describeFloor(policy.floor_seconds) : 'the floor'} after its last ring. That
            is when it becomes dialable, not when it dials: it still needs its calling window open,
            and a lead already claimed waits for a pass to hand the claim back. Leads at{' '}
            {policy?.max_rings ?? 'the cap'} rings are left alone.
          </Hint>
        </div>
      )}

      {preview && movable !== null && movable > 0 && (
        <Fold summary="What moves, and when">
          <p className="text-muted-foreground text-xs tabular-nums">
            <b className="text-foreground">{preview.window_open.toLocaleString()}</b> land inside an
            open calling window
            {preview.awaiting_window > 0 && (
              <>
                {' · '}
                <b className="text-foreground">{preview.awaiting_window.toLocaleString()}</b> wait
                for their window to open
              </>
            )}
            {preview.capped > 0 && (
              <>
                {' · '}
                {preview.capped.toLocaleString()} at {policy?.max_rings ?? 'the cap'} rings stay put
              </>
            )}
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            A released lead dials once the next pass hands its claim back. This says when they
            become dialable, not when a phone rings.
          </p>
        </Fold>
      )}

      {confirming ? (
        <>
          <p className="text-sm">
            Ring the leads this pool is holding on their ladder, as soon as they are allowed to be
            rung again. A lead we may have just spoken to still waits out a short floor, and one
            that has already had its full run of rings is left alone.
          </p>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={release}>
              {busy ? 'Releasing…' : 'Release them'}
            </Button>
          </div>
        </>
      ) : movable === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nothing is parked on the ladder that a release could pull forward.
        </p>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground text-sm">
            {movable === null ? 'Leads waiting on the ring ladder while SDRs sit idle?' : ''}
          </span>
          <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
            <FastForward />
            {movable === null
              ? 'Release ring ladder'
              : `Release ${movable.toLocaleString()} ${movable === 1 ? 'lead' : 'leads'}`}
          </Button>
        </div>
      )}

      {error && (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
          {error}
        </div>
      )}
      {result && <p className="text-sm">{result}</p>}
    </div>
  );
}

// The floor is operator config that moves without a deploy, so it is described from what
// came back rather than named in the copy.
function describeFloor(seconds: number): string {
  if (seconds >= 3600 && seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const minutes = Math.max(1, Math.round(seconds / 60));
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

// The pool's dialled contacts broken into one disposition each — humans / voicemails /
// manual drops (connected), abandoned / busy / no-answer / failed (dialed, never
// connected) — counted by distinct contact (the core's GET /pools/:id/outcomes).
// Percentages are shares of contacts DIALLED, not of the pool: what's still to dial is
// queue state and lives on the Availability tab, so the core's `not_yet_called` count
// feeds the denominator here but no longer renders as a bucket of its own. Fetched on
// open; a single cheap aggregate, so no paging — just the numbers, their split, and a
// Refresh.
function PoolOutcomes({ pool }: { pool: AdminPool }) {
  const [data, setData] = useState<PoolOutcomes | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    fetchPoolOutcomes(pool.id)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [pool.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Contacts this pool has rung, plus `carried` (dialled under a source pool before a
  // merge) — the denominator, so the bars read as shares of calls made and sum to ~100.
  const dialled = data ? Math.max(0, data.total - data.not_yet_called) : 0;
  const pct = (n: number): number => (dialled > 0 ? Math.round((n / dialled) * 100) : 0);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {err ? (
        <p className="text-destructive text-sm">{err}</p>
      ) : !data ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : data.total === 0 ? (
        <p className="text-muted-foreground text-sm">No contacts in this pool yet.</p>
      ) : dialled === 0 ? (
        <p className="text-muted-foreground text-sm">
          No calls placed yet &mdash; the Availability tab shows what&rsquo;s ready to dial.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <OutcomeRow label="Humans" count={data.humans} pct={pct(data.humans)} color="#16a34a" />
          <OutcomeRow
            label="Voicemails"
            count={data.voicemails}
            pct={pct(data.voicemails)}
            color="#d97706"
          />
          {/* SDR pressed Voicemail on a call AMD bridged as a human — the AMD-miss
                rate. Split from Voicemails (AMD-detected); together they're the old
                combined voicemail count. `?? 0` covers the deploy window where the
                new SPA reads a core that doesn't send the field yet. */}
          <OutcomeRow
            label="Manual drops (AMD miss)"
            count={data.manual_drops ?? 0}
            pct={pct(data.manual_drops ?? 0)}
            color="#b45309"
          />
          <OutcomeRow
            label="Abandoned"
            count={data.abandoned}
            pct={pct(data.abandoned)}
            color="#dc2626"
          />
          <OutcomeRow label="Busy" count={data.busy} pct={pct(data.busy)} color="#a855f7" />
          <OutcomeRow
            label="No answer"
            count={data.no_answer}
            pct={pct(data.no_answer)}
            color="#64748b"
          />
          <OutcomeRow label="Failed" count={data.failed} pct={pct(data.failed)} color="#78716c" />
          {/* Dialed before this pool was merged: the outcome lives under the source
                pool, so we can't split it here. Only shown when non-zero (0 on any pool
                that was never a merge survivor). `?? 0` covers the deploy window where a
                newer SPA reads a core that doesn't send the field yet. */}
          {(data.carried ?? 0) > 0 && (
            <OutcomeRow
              label="Dialed before merge"
              count={data.carried}
              pct={pct(data.carried)}
              color="#0ea5e9"
              title="These contacts were dialed while in a source pool, before this pool was merged. Their specific outcome (human / voicemail / …) sits under that source pool, so it isn't broken out here."
            />
          )}
          <div className="text-muted-foreground border-t pt-2 text-xs">
            {dialled.toLocaleString()} of {data.total.toLocaleString()}{' '}
            {data.total === 1 ? 'contact' : 'contacts'} dialled · every dialled contact counts once,
            so the buckets above account for all of them (a voicemail that briefly flashed a card
            counts as a voicemail — under Manual drops when the SDR flagged it, Voicemails when AMD
            did). What&rsquo;s still to dial is on the Availability tab.
          </div>
        </div>
      )}
    </div>
  );
}

function PoolEventsTab({ pool, sdrs }: { pool: AdminPool; sdrs: AdminSdr[] }) {
  // Infinite scroll: events accumulate page by page (newest first). We never
  // hold the whole timeline — each page is fetched from the API as the operator
  // scrolls toward the bottom, keyset-paged by the last (oldest) row's (at, seq).
  const PAGE_SIZE = 100;
  const [events, setEvents] = useState<PoolEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // True once a page comes back short — the timeline's start is reached, no more
  // to fetch.
  const [done, setDone] = useState(false);
  // Default ON — the no_contacts/retry_scheduled pairs are the noisiest events;
  // hide them so the timeline reads as the call/SDR story by default.
  const [hidePacing, setHidePacing] = useState(true);
  // '' = all SDRs; otherwise a stringified sdr_id to filter the timeline by.
  const [sdrFilter, setSdrFilter] = useState<string>('');
  // '' = all; otherwise an exact caller-ID (from) / lead (to) number to filter by.
  const [fromFilter, setFromFilter] = useState<string>('');
  const [toFilter, setToFilter] = useState<string>('');
  // Contact whose details popup is open (from a lead-name click); null = closed.
  const [detailContactId, setDetailContactId] = useState<number | null>(null);
  // Stable, because the modal's dismiss effect keys on it: a fresh identity per render
  // would re-run on every search keystroke and scroll page, pulling focus back each time.
  const closeContactDetails = useCallback(() => setDetailContactId(null), []);
  // The name stays; the record behind it carries email, employer and title.
  const canAdmin = useHasPermission('console:admin');

  // Free-text lead search (name or number). Unlike the dropdowns, the debounced
  // trimmed term drives the SERVER `q` and searches the FULL timeline — the
  // dropdowns only know leads that happen to be in the loaded pages.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  // Guards against firing the next-page fetch while one is already in flight
  // (the IntersectionObserver can re-trigger before React state settles).
  const fetchingRef = useRef(false);
  // Monotonic reset stamp: a search change mid-scroll fires a fresh first-page
  // load while an older fetch may still be in flight — stale responses (an old
  // load, or a loadMore keyed to the previous search) are dropped, never merged.
  const seqRef = useRef(0);
  // Synchronous mirrors of events/done/debouncedSearch. loadMore reads THESE,
  // not its closure: the IntersectionObserver can invoke a previous render's
  // closure in the gap before the effect swaps it, and a stale closure firing
  // right after a reset would otherwise page the OLD list's cursor (with the
  // old q) into the fresh list — the seq stamp can't catch it because the call
  // is made in the current generation.
  const eventsRef = useRef<PoolEvent[]>([]);
  const doneRef = useRef(false);
  const searchRef = useRef('');
  // The scroll container (observer root) and the bottom sentinel it watches.
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Resolve sdr_id -> name for display (events only store the numeric id).
  const sdrName = (id: number | null): string => {
    if (id == null) return '';
    const sdr = sdrs.find((s) => s.id === id);
    return sdr?.name || `SDR #${id}`;
  };

  // A search reset can shrink the loaded set and drop a selected dropdown value
  // from its options — the select would render blank while its filter silently
  // kept applying. Keep the selection listed so it stays visible and clearable.
  const withSelected = <T,>(options: T[], selected: T | null): T[] =>
    selected != null && !options.includes(selected) ? [...options, selected] : options;

  // SDRs that actually appear in the loaded events — the dropdown options. Grows
  // as more pages load.
  const sdrIdsInEvents = withSelected(
    [...new Set(events.map((e) => e.sdr_id).filter((id): id is number => id != null))],
    sdrFilter === '' ? null : Number(sdrFilter),
  );

  // The two number dropdowns' options; they grow as more pages load.
  const fromNumbersInEvents = withSelected(
    [...new Set(events.map((e) => e.from_number).filter((n): n is string => n != null))],
    fromFilter || null,
  );
  const toNumbersInEvents = withSelected(
    [...new Set(events.map((e) => e.to_number).filter((n): n is string => n != null))],
    toFilter || null,
  );
  // Best-known lead name per dialed number, so the Lead dropdown reads as names.
  const leadNameByNumber = new Map<string, string>();
  for (const e of events) {
    if (e.to_number && e.contact_name && !leadNameByNumber.has(e.to_number)) {
      leadNameByNumber.set(e.to_number, e.contact_name);
    }
  }

  // Filtering is client-side over what's loaded; the observer keeps pulling pages
  // when the filtered view is too short to scroll (so a filter hiding most rows
  // doesn't stall paging).
  const visible = events.filter(
    (e) =>
      (!hidePacing || !PACING_EVENTS.has(e.event_type)) &&
      (sdrFilter === '' || String(e.sdr_id) === sdrFilter) &&
      (fromFilter === '' || e.from_number === fromFilter) &&
      (toFilter === '' || e.to_number === toFilter),
  );
  const hiddenCount = events.length - visible.length;

  // Initial load / Refresh / search change: reset to the first (newest) page.
  const load = useCallback(() => {
    const seq = ++seqRef.current;
    fetchingRef.current = true;
    searchRef.current = debouncedSearch;
    doneRef.current = false;
    setLoading(true);
    // A reset supersedes any in-flight "load more" — clear its spinner (its own
    // finally is seq-guarded and won't).
    setLoadingMore(false);
    setErr(null);
    setDone(false);
    fetchPoolEvents(pool.id, { limit: PAGE_SIZE, q: debouncedSearch })
      .then((page) => {
        if (seq !== seqRef.current) return; // superseded by a newer reset
        eventsRef.current = page;
        setEvents(page);
        if (page.length < PAGE_SIZE) {
          doneRef.current = true;
          setDone(true);
        }
      })
      .catch((e) => {
        if (seq !== seqRef.current) return;
        setErr(e instanceof Error ? e.message : String(e));
        // A failed reset must not leave the previous search's rows scrollable:
        // loadMore would page THEIR cursor under the NEW q and merge the two
        // generations. Clear + stop paging; the error banner explains.
        eventsRef.current = [];
        setEvents([]);
        doneRef.current = true;
        setDone(true);
      })
      .finally(() => {
        if (seq !== seqRef.current) return;
        setLoading(false);
        fetchingRef.current = false;
      });
  }, [pool.id, debouncedSearch]);

  // Fetch the next, older page starting after the last loaded row. Cursor, done
  // and q come from the refs (see their declaration): a stale-closure invocation
  // then behaves exactly like a fresh one.
  const loadMore = useCallback(() => {
    const seq = seqRef.current;
    const list = eventsRef.current;
    const last = list[list.length - 1];
    if (fetchingRef.current || doneRef.current || !last) return;
    const before: PoolEventsCursor = { at: last.at, seq: last.seq };
    fetchingRef.current = true;
    setLoadingMore(true);
    setErr(null);
    fetchPoolEvents(pool.id, { limit: PAGE_SIZE, before, q: searchRef.current })
      .then((page) => {
        if (seq !== seqRef.current) return; // a reset landed mid-flight
        eventsRef.current = [...eventsRef.current, ...page];
        setEvents(eventsRef.current);
        if (page.length < PAGE_SIZE) {
          doneRef.current = true;
          setDone(true);
        }
      })
      .catch((e) => {
        if (seq !== seqRef.current) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        // Superseded: the reset already cleared the spinner and owns the
        // in-flight guard — touch neither.
        if (seq !== seqRef.current) return;
        setLoadingMore(false);
        fetchingRef.current = false;
      });
    // events/done/debouncedSearch are read via refs, but stay in the deps so the
    // observer effect below re-arms after each page — a persistent observer
    // stops firing while the sentinel merely STAYS visible.
  }, [pool.id, done, events, debouncedSearch]);

  useEffect(() => load(), [load]);

  // Watch the bottom sentinel: when it scrolls into view, pull the next page.
  // Re-created whenever loadMore changes (i.e. after each page) so it keeps
  // firing while the sentinel stays visible — fills the viewport or exhausts.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || done) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: '200px' },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [loadMore, done]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {/* Filter bar: lead search (server-side, full timeline) + by SDR /
            caller ID / lead dropdowns + the waiting-events toggle. */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search lead name or number…"
          title="Searches the pool's whole timeline, not just the loaded events"
          className="border-input min-w-44 flex-1 rounded-md border px-2 py-1"
        />
        <label className="text-muted-foreground flex items-center gap-1.5">
          SDR
          <select
            value={sdrFilter}
            onChange={(e) => setSdrFilter(e.target.value)}
            className="rounded border px-2 py-1 text-xs"
          >
            <option value="">All SDRs</option>
            {sdrIdsInEvents.map((id) => (
              <option key={id} value={String(id)}>
                {sdrName(id)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-muted-foreground flex items-center gap-1.5">
          Caller ID
          <select
            value={fromFilter}
            onChange={(e) => setFromFilter(e.target.value)}
            className="rounded border px-2 py-1 font-mono text-xs"
          >
            <option value="">All caller IDs</option>
            {fromNumbersInEvents.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label className="text-muted-foreground flex items-center gap-1.5">
          Lead
          <select
            value={toFilter}
            onChange={(e) => setToFilter(e.target.value)}
            className="rounded border px-2 py-1 text-xs"
          >
            <option value="">All leads</option>
            {toNumbersInEvents.map((n) => (
              <option key={n} value={n}>
                {leadNameByNumber.get(n) ?? n}
              </option>
            ))}
          </select>
        </label>
        <label
          className="text-muted-foreground flex cursor-pointer items-center gap-1.5 select-none"
          title="Hide the repeating no_contacts / retry_scheduled events — when the dialer has nothing to call yet and re-checks every few seconds"
        >
          <input
            type="checkbox"
            checked={hidePacing}
            onChange={(e) => setHidePacing(e.target.checked)}
          />
          Hide waiting events
        </label>
      </div>

      {err && <p className="text-destructive text-xs">⚠ {err}</p>}

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {/* Gated on !err: a failed reset clears the list, and "no events" /
              "no match" would be a false claim next to the error banner. */}
        {events.length === 0 && !loading && !err && (
          <p className="text-muted-foreground py-6 text-center text-sm italic">
            {debouncedSearch
              ? `No events match “${debouncedSearch}” — only call events carry a lead, and the search covers the whole timeline.`
              : 'No events yet for this pool.'}
          </p>
        )}
        {events.length > 0 && visible.length === 0 && done && (
          <p className="text-muted-foreground py-6 text-center text-sm italic">
            All {hiddenCount} loaded events are waiting events — untick “Hide waiting events” to see
            them.
          </p>
        )}
        <ul className="space-y-1">
          {visible.map((e) => (
            <li
              key={`${e.at}-${e.seq}`}
              className="flex items-center gap-2 border-b py-1.5 text-xs last:border-b-0"
            >
              <span className="text-muted-foreground w-48 flex-none whitespace-nowrap font-mono text-[0.68rem] tabular-nums">
                {formatEventTime(e.at)}
              </span>
              <span
                className={cn(
                  'flex-none rounded px-1.5 py-0.5 font-medium',
                  EVENT_STYLE[e.event_type] ?? 'bg-gray-100 text-gray-700',
                )}
              >
                {e.event_type}
              </span>
              <span
                className="text-muted-foreground min-w-0 flex-1 truncate"
                title={e.detail ?? undefined}
              >
                {e.sdr_id != null && <span className="font-medium">{sdrName(e.sdr_id)}</span>}
                {e.sdr_id != null && (e.contact_name || e.detail) && ' · '}
                {e.contact_name && (
                  <>
                    {e.contact_id != null && canAdmin ? (
                      <button
                        type="button"
                        onClick={() => setDetailContactId(e.contact_id)}
                        className="text-primary cursor-pointer font-medium underline-offset-4 hover:underline"
                        title="Contact details"
                      >
                        {e.contact_name}
                      </button>
                    ) : (
                      <span className="font-medium">{e.contact_name}</span>
                    )}
                    {e.detail && ' · '}
                  </>
                )}
                {e.detail && <span className="font-mono">{e.detail}</span>}
              </span>
            </li>
          ))}
        </ul>

        {/* Sentinel the IntersectionObserver watches to pull the next page. */}
        <div ref={sentinelRef} aria-hidden className="h-px" />

        {loadingMore && (
          <p className="text-muted-foreground py-3 text-center text-xs italic">Loading more…</p>
        )}
        {done && events.length > 0 && (
          <p className="text-muted-foreground py-3 text-center text-[0.68rem] italic">
            Start of timeline.
          </p>
        )}
      </div>

      <p className="text-muted-foreground text-[0.68rem]">
        Newest first · showing {visible.length} of {events.length} loaded event
        {events.length === 1 ? '' : 's'}
        {debouncedSearch && ` matching “${debouncedSearch}”`}
        {!done && ' (scroll for more)'}
        {hidePacing && hiddenCount > 0 && ` · ${hiddenCount} waiting hidden`}.
      </p>

      {detailContactId != null && (
        <ContactDetailsModal contactId={detailContactId} onClose={closeContactDetails} />
      )}
    </div>
  );
}

// The pool outcomes viewer. Keyset infinite scroll like the events tab, but the list
// comes from the api, so paging uses its opaque `nextCursor` rather than a computed
// (at, seq). Newest first; each row is the classified outcome, who answered, and the
// reasoning behind it.
function PoolDispositionsTab({ pool }: { pool: AdminPool }) {
  const PAGE_SIZE = 100;
  const [items, setItems] = useState<PoolDisposition[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [done, setDone] = useState(false);

  const fetchingRef = useRef(false);
  // Load generation: each Refresh (load) bumps it. A fetch captures the generation at
  // start and drops its result if a newer load has since replaced the list — so a
  // still-in-flight loadMore can never append stale rows onto a refreshed list.
  const loadSeqRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Initial load / Refresh: reset to the first (newest) page.
  const load = useCallback(() => {
    const seq = ++loadSeqRef.current;
    fetchingRef.current = true;
    setLoading(true);
    setLoadingMore(false); // a Refresh supersedes any in-flight "load more"
    setErr(null);
    setDone(false);
    fetchPoolDispositions(pool.id, { limit: PAGE_SIZE })
      .then((page) => {
        if (seq !== loadSeqRef.current) return; // superseded by a newer load
        setItems(page.items);
        setCursor(page.nextCursor);
        setDone(!page.nextCursor);
      })
      .catch((e) => {
        if (seq === loadSeqRef.current) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (seq !== loadSeqRef.current) return;
        setLoading(false);
        fetchingRef.current = false;
      });
  }, [pool.id]);

  // Fetch the next, older page from the api's opaque cursor.
  const loadMore = useCallback(() => {
    if (fetchingRef.current || done || !cursor) return;
    const seq = loadSeqRef.current; // tie this page to the current generation
    fetchingRef.current = true;
    setLoadingMore(true);
    setErr(null);
    fetchPoolDispositions(pool.id, { limit: PAGE_SIZE, cursor })
      .then((page) => {
        if (seq !== loadSeqRef.current) return; // a Refresh replaced the list — drop this append
        setItems((prev) => [...prev, ...page.items]);
        setCursor(page.nextCursor);
        setDone(!page.nextCursor);
      })
      .catch((e) => {
        if (seq === loadSeqRef.current) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (seq !== loadSeqRef.current) return; // superseded — the newer load owns the flags
        setLoadingMore(false);
        fetchingRef.current = false;
      });
  }, [pool.id, done, cursor]);

  useEffect(() => load(), [load]);

  // Watch the bottom sentinel: pull the next page when it scrolls into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || done) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: '200px' },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [loadMore, done]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {err && <p className="text-destructive text-xs">⚠ {err}</p>}

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {items.length === 0 && !loading && (
          <p className="text-muted-foreground py-6 text-center text-sm italic">
            No outcomes yet for this pool. A call appears here once the classifier has settled it.
          </p>
        )}
        {items.length > 0 && (
          <div className="bg-card text-muted-foreground sticky top-0 z-10 flex items-center gap-3 border-b px-2 py-1.5 text-[0.62rem] font-semibold tracking-wide uppercase">
            <span className="w-40 flex-none">Time</span>
            <span className="w-44 flex-none">Disposition</span>
            <span className="w-20 flex-none">Answered</span>
            <span className="min-w-0 flex-1">Reasoning</span>
          </div>
        )}
        <ul>
          {items.map((d) => {
            const reason = formatDisposition(d.detail).detail;
            const label = d.label || '—';
            return (
              <li
                key={d.callId}
                className="hover:bg-muted/40 flex items-center gap-3 rounded-sm px-2 py-2 text-xs transition-colors"
              >
                <span className="text-muted-foreground w-40 flex-none whitespace-nowrap font-mono text-[0.68rem] tabular-nums">
                  {formatEventTime(d.at)}
                </span>
                <span className="w-44 flex-none">
                  <span
                    className={`${dispositionStyle(label)} inline-block max-w-full truncate rounded-full px-2 py-0.5 text-[0.7rem] font-semibold`}
                    title={label}
                  >
                    {label}
                  </span>
                </span>
                <span className="w-20 flex-none">
                  <AnsweredByBadge answeredBy={d.answeredBy} />
                </span>
                <span
                  className={`min-w-0 flex-1 truncate ${reason ? 'text-foreground/80' : 'text-muted-foreground/50'}`}
                  title={reason ?? undefined}
                >
                  {reason ?? '—'}
                </span>
              </li>
            );
          })}
        </ul>

        {/* Sentinel the IntersectionObserver watches to pull the next page. */}
        <div ref={sentinelRef} aria-hidden className="h-px" />

        {loadingMore && (
          <p className="text-muted-foreground py-3 text-center text-xs italic">Loading more…</p>
        )}
        {done && items.length > 0 && (
          <p className="text-muted-foreground py-3 text-center text-[0.68rem] italic">
            End of list.
          </p>
        )}
      </div>

      <p className="text-muted-foreground text-[0.68rem]">
        Newest first · {items.length} loaded{!done && ' (scroll for more)'}.
      </p>
    </div>
  );
}

// A candidate recording enriched with S3 truth: `exists` (a playable WAV is stored;
// false rows are hidden), `durationSec` (the recording's real length, which the
// core's call-leg `duration` gets wrong), and `disposition` (null until the call has
// been classified). Filter-support fields are precomputed at enrich time so the
// per-keystroke filter pass does string compares only, never JSON.parse per row.
type EnrichedRecording = PoolRecording &
  RecordingStatus & {
    disposition: string | null;
    // Stable filter key + display label for the disposition (null = unclassified).
    dispositionKey: string | null;
    dispositionLabel: string | null;
  };

// Keys on the machine id, not `label`: labels are editable operator config
// snapshotted into each row at classification time, so keying on them would split
// one disposition into several dropdown entries after a rename. Free-text blobs
// carrying no id fall back to the normalized label.
function dispositionFilterKey(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as { disposition?: unknown };
    if (typeof d.disposition === 'string') return d.disposition;
  } catch {
    // Not JSON — fall through to the label form.
  }
  return formatDisposition(raw).label.trim().toLowerCase();
}

// One recording row: contact/SDR/time/duration + a lazily-loaded inline player and
// an on-demand transcript. We don't presign or fetch the transcript on open (that
// would do work for every row, most never opened) — the first Play click fetches the
// presigned URL, swaps in a real <audio>, and autoplays; the first Transcript click
// fetches the diarized envelope and opens it in a scrollable modal. Both cache after
// load, so re-opening the modal is instant. A 404 on either (no stored artifact yet —
// the jobs pipeline lags the call by a beat) shows inline (playback) or in the modal
// (transcript). `duration` shown is the exact S3 length, not the call-leg one.
function RecordingRow({
  rec,
  sdrName,
}: {
  rec: EnrichedRecording;
  sdrName: (id: number | null) => string;
}) {
  // The contact name rides on the recording row now (resolved server-side); fall
  // back to the id when it's null (unnamed contact), matching the old behavior.
  const contactLabel = rec.contact_name || `Contact #${rec.contact_id}`;
  const disposition = formatDisposition(rec.disposition);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Transcript is fetched lazily on first open (like the recording's presign) and
  // then kept; the button opens/closes the modal without re-fetching.
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [tLoading, setTLoading] = useState(false);
  const [tErr, setTErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    recordingUrl(rec.call_id)
      .then((r) => setUrl(r.url))
      .catch((e) => {
        const status = e instanceof AdminRequestError ? e.status : 0;
        setErr(
          status === 404
            ? 'Recording not available yet.'
            : status === 503
              ? 'Playback not available in this environment.'
              : 'Could not load recording.',
        );
      })
      .finally(() => setLoading(false));
  }, [rec.call_id]);

  // Fetch the transcript (clears any prior error, so it doubles as Retry). 404 = not
  // transcribed yet; 503 = the api tier isn't wired in this env (same as playback).
  const fetchTranscriptNow = useCallback(() => {
    setTLoading(true);
    setTErr(null);
    fetchTranscript(rec.call_id)
      .then(setTranscript)
      .catch((e) => {
        const status = e instanceof AdminRequestError ? e.status : 0;
        setTErr(
          status === 404
            ? 'Transcript not available yet.'
            : status === 503
              ? 'Transcripts not available in this environment.'
              : 'Could not load transcript.',
        );
      })
      .finally(() => setTLoading(false));
  }, [rec.call_id]);

  const openTranscript = useCallback(() => {
    // Fetch on the first open only when we don't already have it (or an error to
    // retry); a cached transcript makes re-opening instant. The side-effect stays
    // OUTSIDE any state updater (updaters must be pure — StrictMode double-invokes them
    // in dev, which would otherwise fire the fetch twice).
    if (!transcript && !tErr && !tLoading) fetchTranscriptNow();
    setModalOpen(true);
  }, [transcript, tErr, tLoading, fetchTranscriptNow]);

  // Stable so TranscriptModal's focus/Escape effect isn't re-run on every row re-render
  // (e.g. as the transcript fetch state settles).
  const closeModal = useCallback(() => setModalOpen(false), []);

  return (
    <li className="border-b py-2 text-xs last:border-b-0">
      <div className="flex items-center gap-3">
        <span className="text-muted-foreground w-36 flex-none font-mono text-[0.68rem] tabular-nums">
          {formatEventTime(rec.at)}
        </span>
        {/* Lead block: name with the dialed number stacked under it (mirroring the
            SDR block beside it), so near-identical names tell apart at a glance. */}
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{contactLabel}</span>
          <span className="text-muted-foreground block truncate font-mono text-[0.68rem] tabular-nums">
            {rec.contact_phone || '—'}
          </span>
        </span>
        <span className="text-muted-foreground min-w-0 flex-1 truncate">{sdrName(rec.sdr_id)}</span>
        <span className="text-muted-foreground w-12 flex-none text-right tabular-nums">
          {formatDuration(rec.durationSec)}
        </span>
        <span className="w-56 flex-none text-right">
          {url ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- a sales call recording has no captions track
            <audio src={url} controls autoPlay className="h-8 w-full" />
          ) : err ? (
            <span className="text-muted-foreground inline-flex items-center gap-2 italic">
              {err}
              <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                Retry
              </Button>
            </span>
          ) : (
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              {loading ? 'Loading…' : '▶ Play'}
            </Button>
          )}
        </span>
        {/* The classified disposition, inline next to the Transcript button. Shows the
            friendly disposition label; the classifier's reasoning + notes are in the
            hover tooltip. Shrinkable (min-w-0 + max cap) so narrow viewports never
            overflow the row; "—" until the call has been classified. */}
        <span
          className="text-muted-foreground min-w-0 max-w-56 flex-1 truncate text-[0.7rem]"
          title={disposition.detail}
        >
          {disposition.label}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="w-32 flex-none"
          onClick={openTranscript}
          aria-haspopup="dialog"
          title="Read the diarized transcript of this call"
        >
          <FileText className="size-3.5" />
          Transcript
        </Button>
      </div>

      {modalOpen &&
        createPortal(
          <TranscriptModal
            contact={contactLabel}
            sdr={sdrName(rec.sdr_id)}
            at={rec.at}
            durationSec={rec.durationSec}
            transcript={transcript}
            loading={tLoading}
            error={tErr}
            onRetry={fetchTranscriptNow}
            onClose={closeModal}
          />,
          document.body,
        )}
    </li>
  );
}

// The pool recordings viewer. Mirrors PoolEventsTab's keyset infinite scroll.
// The core lists CANDIDATE calls (those that bridged to an SDR) newest-first, paged;
// for each page we enrich via the api (S3 HEAD) to learn which candidates actually
// have a stored WAV + its exact length, then show only the real ones. Each row plays
// inline on demand (lazy presign).
function PoolRecordingsTab({ pool, sdrs }: { pool: AdminPool; sdrs: AdminSdr[] }) {
  const PAGE_SIZE = 100;
  // Candidates accumulate page by page (newest first), each enriched with S3 truth.
  // We keep the RAW candidate list (incl. non-existent recordings) so the keyset
  // cursor never skips a row; `visible` is the filtered view actually rendered.
  const [candidates, setCandidates] = useState<EnrichedRecording[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // True once a candidate page comes back short — no older candidates to fetch.
  const [done, setDone] = useState(false);

  // The lead search runs SERVER-SIDE against the pool's full recordings list:
  // the debounced trimmed term resolves to contact ids in Postgres, so it isn't
  // limited to the pages already loaded. The SDR/disposition dropdowns stay
  // client-side over the loaded rows; a dropdown that empties the view leaves
  // the scroll sentinel visible, so the observer keeps fetching older pages
  // until matches appear or candidates run out.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);
  // 'all' | 'none' (unassigned) | String(sdr_id)
  const [sdrFilter, setSdrFilter] = useState('all');
  // 'all' | 'none' (unclassified) | a stable disposition key
  const [dispositionFilter, setDispositionFilter] = useState('all');
  // True once a disposition-status fetch fails: null dispositions then mean
  // "unknown" rather than "unclassified", so filtering on them would lie. The
  // dropdown is disabled instead.
  const [dispositionsUnavailable, setDispositionsUnavailable] = useState(false);

  // Guards against firing the next-page fetch while one is in flight (the observer
  // can re-trigger before React state settles).
  const fetchingRef = useRef(false);
  // Monotonic reset stamp: a search change mid-scroll fires a fresh first-page
  // load while an older fetch may still be in flight — stale responses (an old
  // load, or a loadMore keyed to the previous search) are dropped, never merged.
  const seqRef = useRef(0);
  // Synchronous mirrors of candidates/done/debouncedSearch — loadMore reads
  // these, not its closure, so a stale-closure invocation from the observer
  // can't page the old list's cursor into a fresh list (see PoolEventsTab's
  // identical refs for the full rationale).
  const candidatesRef = useRef<EnrichedRecording[]>([]);
  const doneRef = useRef(false);
  const searchRef = useRef('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Resolve the SDR id -> name for display (the core returns ids only, like events).
  // The contact name rides on each recording row now (rec.contact_name), so there's
  // no contacts lookup here — the row resolves its own label.
  const sdrName = (id: number | null): string => {
    if (id == null) return 'Unassigned SDR';
    return sdrs.find((s) => s.id === id)?.name || `SDR #${id}`;
  };

  // This pool's assigned SDRs plus any SDR seen in the loaded rows: recordings
  // outlive assignments, so a since-unassigned SDR stays filterable. A search
  // reset can shrink the loaded set and drop the selected id — keep it listed so
  // the select never renders blank while its filter still applies.
  const sdrIds = new Set<number>(sdrs.filter((s) => s.pool_ids.includes(pool.id)).map((s) => s.id));
  for (const c of candidates) if (c.sdr_id != null) sdrIds.add(c.sdr_id);
  if (sdrFilter !== 'all' && sdrFilter !== 'none') sdrIds.add(Number(sdrFilter));
  const sdrOptions = [...sdrIds]
    .map((id) => ({ id, name: sdrName(id) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // The distinct dispositions among loaded playable rows. Newest-first iteration
  // means a renamed disposition shows its latest label. Like the SDR options,
  // the selected key survives a search reset that dropped its rows.
  const dispositionsByKey = new Map<string, string>();
  for (const c of candidates) {
    if (!c.exists || c.dispositionKey == null || dispositionsByKey.has(c.dispositionKey)) continue;
    dispositionsByKey.set(c.dispositionKey, c.dispositionLabel ?? c.dispositionKey);
  }
  if (
    dispositionFilter !== 'all' &&
    dispositionFilter !== 'none' &&
    !dispositionsByKey.has(dispositionFilter)
  ) {
    dispositionsByKey.set(dispositionFilter, dispositionFilter);
  }
  const dispositionOptions = [...dispositionsByKey.entries()].sort((a, b) =>
    a[1].localeCompare(b[1]),
  );

  // The search already narrowed the list server-side; only the SDR/disposition
  // dropdowns filter here, over the loaded rows.
  const playable = candidates.filter((c) => c.exists);
  // Force the filter off when the data is unavailable: every null disposition then
  // means "unknown", and filtering on that would drop rows that may well match.
  const dispoFilter = dispositionsUnavailable ? 'all' : dispositionFilter;
  const filtersActive = debouncedSearch !== '' || sdrFilter !== 'all' || dispoFilter !== 'all';
  const visible = playable.filter((c) => {
    if (sdrFilter !== 'all') {
      if (sdrFilter === 'none' ? c.sdr_id != null : String(c.sdr_id) !== sdrFilter) return false;
    }
    if (dispoFilter !== 'all') {
      if (dispoFilter === 'none' ? c.dispositionKey != null : c.dispositionKey !== dispoFilter)
        return false;
    }
    return true;
  });

  // Enrich a candidate page with S3 truth (exists + exact length) + any generated
  // disposition. Enrichment is an ENHANCEMENT: if the api tier is unwired/unavailable
  // (e.g. local dev without the api, → 503), we don't blank the list — we fall back to
  // showing every candidate with an unknown length, i.e. the pre-enrichment behavior.
  // Dispositions are best-effort on top (a failed/empty fetch just leaves them null).
  const enrich = useCallback(
    async (page: PoolRecording[], seqAtCall: number): Promise<EnrichedRecording[]> => {
      const callIds = page.map((r) => r.call_id);
      // Both enrichments run CONCURRENTLY — dispositions are a best-effort overlay and
      // must never delay the primary recording-status fetch (which decides visibility).
      // Each has its own fallback: recording-status failure → show all candidates with
      // unknown length; disposition failure/empty → leave dispositions null.
      const [disps, rec] = await Promise.all([
        fetchDispositionStatuses(callIds).catch(() => {
          // Only the CURRENT generation may disable the dropdown — a superseded
          // fetch failing late must not clobber a newer load's successful state.
          if (seqRef.current === seqAtCall) setDispositionsUnavailable(true);
          return {} as Record<string, DispositionStatus>;
        }),
        fetchRecordingStatuses(callIds)
          .then((s) => ({ ok: true as const, s }))
          .catch((e) => {
            console.warn('recording status enrichment unavailable; showing all candidates', e);
            return { ok: false as const };
          }),
      ]);
      return page.map((r) => {
        const disposition = disps[r.call_id]?.disposition ?? null;
        return {
          ...r,
          ...(rec.ok
            ? (rec.s[r.call_id] ?? { exists: false, durationSec: null })
            : { exists: true, durationSec: null }),
          disposition,
          dispositionKey: dispositionFilterKey(disposition),
          dispositionLabel: disposition ? formatDisposition(disposition).label : null,
        };
      });
    },
    [],
  );

  // Initial load / Refresh / search change: reset to the first (newest) page.
  const load = useCallback(() => {
    const seq = ++seqRef.current;
    fetchingRef.current = true;
    searchRef.current = debouncedSearch;
    doneRef.current = false;
    setLoading(true);
    // A reset supersedes any in-flight "load more" — clear its spinner (its own
    // finally is seq-guarded and won't).
    setLoadingMore(false);
    setErr(null);
    setDone(false);
    setDispositionsUnavailable(false);
    fetchPoolRecordings(pool.id, { limit: PAGE_SIZE, q: debouncedSearch })
      .then(async (page) => {
        const enriched = await enrich(page, seq);
        if (seq !== seqRef.current) return; // superseded by a newer reset
        candidatesRef.current = enriched;
        setCandidates(enriched);
        if (page.length < PAGE_SIZE) {
          doneRef.current = true;
          setDone(true);
        }
      })
      .catch((e) => {
        if (seq !== seqRef.current) return;
        setErr(e instanceof Error ? e.message : String(e));
        // A failed reset must not leave the previous search's rows scrollable:
        // loadMore would page THEIR cursor under the NEW q and merge the two
        // generations. Clear + stop paging; the error banner explains.
        candidatesRef.current = [];
        setCandidates([]);
        doneRef.current = true;
        setDone(true);
      })
      .finally(() => {
        if (seq !== seqRef.current) return;
        setLoading(false);
        fetchingRef.current = false;
      });
  }, [pool.id, enrich, debouncedSearch]);

  // Fetch the next, older candidate page starting after the last loaded row.
  // Cursor, done and q come from the refs (see their declaration): a
  // stale-closure invocation then behaves exactly like a fresh one.
  const loadMore = useCallback(() => {
    const seq = seqRef.current;
    const list = candidatesRef.current;
    const last = list[list.length - 1];
    if (fetchingRef.current || doneRef.current || !last) return;
    const before: PoolRecordingsCursor = { at: last.at, call_id: last.call_id };
    fetchingRef.current = true;
    setLoadingMore(true);
    setErr(null);
    fetchPoolRecordings(pool.id, { limit: PAGE_SIZE, before, q: searchRef.current })
      .then(async (page) => {
        const enriched = await enrich(page, seq);
        if (seq !== seqRef.current) return; // a reset landed mid-flight
        candidatesRef.current = [...candidatesRef.current, ...enriched];
        setCandidates(candidatesRef.current);
        if (page.length < PAGE_SIZE) {
          doneRef.current = true;
          setDone(true);
        }
      })
      .catch((e) => {
        if (seq !== seqRef.current) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        // Superseded: the reset already cleared the spinner and owns the
        // in-flight guard — touch neither.
        if (seq !== seqRef.current) return;
        setLoadingMore(false);
        fetchingRef.current = false;
      });
    // candidates/done/debouncedSearch are read via refs, but stay in the deps so
    // the observer effect below re-arms after each page — a persistent observer
    // stops firing while the sentinel merely STAYS visible.
  }, [pool.id, done, candidates, enrich, debouncedSearch]);

  useEffect(() => load(), [load]);

  // Pull the next page when the bottom sentinel scrolls into view. A page whose
  // candidates mostly lack a WAV, or a dropdown filter matching nothing, leaves the
  // sentinel visible and keeps the observer pulling. Worst case is one sequential
  // walk of the pool's history, at most once per open: pages accumulate in
  // `candidates`, so dropdown changes re-filter in memory — a search change instead
  // resets and re-fetches (it narrows server-side).
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || done) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: '200px' },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [loadMore, done]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search lead name or number…"
          title="Searches the pool's whole recording history, not just the loaded rows"
          className="border-input min-w-48 flex-1 rounded-md border px-2 py-1.5 text-sm"
        />
        <select
          value={sdrFilter}
          onChange={(e) => setSdrFilter(e.target.value)}
          className="rounded border px-2 py-1 text-xs"
          title="Only calls taken by this SDR"
        >
          <option value="all">All SDRs</option>
          <option value="none">Unassigned SDR</option>
          {sdrOptions.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={dispoFilter}
          onChange={(e) => setDispositionFilter(e.target.value)}
          className="rounded border px-2 py-1 text-xs disabled:opacity-50"
          disabled={dispositionsUnavailable}
          title={
            dispositionsUnavailable
              ? 'Dispositions could not be loaded — filter unavailable'
              : 'Only calls classified with this disposition'
          }
        >
          <option value="all">All dispositions</option>
          <option value="none">None yet</option>
          {dispositionOptions.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {err && <p className="text-destructive text-xs">⚠ {err}</p>}

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {/* Gated on !err: a failed reset clears the list, and "no recordings" /
              "no match" would be a false claim next to the error banner. */}
        {candidates.length === 0 && !loading && !err && (
          <p className="text-muted-foreground py-6 text-center text-sm italic">
            {debouncedSearch
              ? `No recorded calls match “${debouncedSearch}” — the search covers the pool's whole history.`
              : 'No recordings yet for this pool. Recordings appear once a call is answered and the WAV is ingested.'}
          </p>
        )}
        {candidates.length > 0 &&
          visible.length === 0 &&
          (filtersActive ? (
            <p className="text-muted-foreground py-6 text-center text-sm italic">
              No recordings match the current filters
              {done ? '.' : ' yet — checking older calls…'}
            </p>
          ) : (
            done && (
              <p className="text-muted-foreground py-6 text-center text-sm italic">
                None of this pool's answered calls produced a stored recording (voicemail stubs and
                failed bridges leave no WAV).
              </p>
            )
          ))}
        <ul>
          {visible.map((rec) => (
            <RecordingRow key={rec.call_id} rec={rec} sdrName={sdrName} />
          ))}
        </ul>

        {/* Sentinel the IntersectionObserver watches to pull the next page. */}
        <div ref={sentinelRef} aria-hidden className="h-px" />

        {loadingMore && (
          <p className="text-muted-foreground py-3 text-center text-xs italic">Loading more…</p>
        )}
        {done && visible.length > 0 && (
          <p className="text-muted-foreground py-3 text-center text-[0.68rem] italic">
            End of recordings.
          </p>
        )}
      </div>

      <p className="text-muted-foreground text-[0.68rem]">
        Newest first ·{' '}
        {sdrFilter !== 'all' || dispoFilter !== 'all'
          ? `${visible.length} of ${playable.length} recording${playable.length === 1 ? '' : 's'} matching`
          : `${visible.length} recording${visible.length === 1 ? '' : 's'}`}
        {debouncedSearch && ` · matching “${debouncedSearch}”`}
        {!done && ' (scroll for more)'} · click ▶ Play to listen, Transcript to read.
      </p>
    </div>
  );
}

// A figure the analytics store could not answer for. Held in place rather than hidden:
// a missing badge reads as "nothing to report", the one thing this cannot say.
function UnreadStat({ label, icon }: { label: string; icon?: ReactNode }) {
  return (
    <span
      className="text-muted-foreground/70 inline-flex items-center gap-1 text-sm"
      title="The analytics store is unreachable, so this figure can't be read right now."
    >
      {icon}
      <b>—</b> {label}
    </span>
  );
}

// Overview: what this pool has cost and how often it reached a person, over the
// outcome breakdown. These three numbers were on the card, where they were three
// of eleven things competing for the same strip.
function PoolOverviewTab({ pool }: { pool: AdminPool }) {
  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {pool.total_dials === null || pool.human_pickups === null ? (
          <UnreadStat label="pickup" />
        ) : (
          pool.total_dials > 0 && (
            <span
              className="text-muted-foreground inline-flex items-center gap-1 text-sm tabular-nums"
              title="Pickup rate: contacts that reached a real person (the Humans row below) ÷ total dials, for calls placed under this pool. On a merged pool that's this pool's own era, not the folded sources'. Calls AMD ruled a voicemail don't count as pickups."
            >
              <b className="text-foreground">
                {Math.round(
                  (Math.min(pool.human_pickups, pool.total_dials) / pool.total_dials) * 100,
                )}
                %
              </b>
              <span>
                pickup · {Math.min(pool.human_pickups, pool.total_dials)}/{pool.total_dials}
              </span>
            </span>
          )
        )}
        {pool.llm_spend_micros === null ? (
          <UnreadStat label="LLM" icon={<Sparkles className="h-3.5 w-3.5" />} />
        ) : (
          pool.llm_spend_micros > 0 && (
            <span
              className="text-muted-foreground inline-flex items-center gap-1 text-sm tabular-nums"
              title="LLM cost for this pool's AI call dispositions, at list prices — for calls placed under this pool (a merged pool's own era, not the folded sources')"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <b className="text-foreground">{formatSpend(pool.llm_spend_micros)}</b> LLM
            </span>
          )
        )}
        {pool.twilio_spend_micros === null ? (
          <UnreadStat label="Telephony" icon={<Phone className="h-3.5 w-3.5" />} />
        ) : (
          pool.twilio_spend_micros > 0 && (
            <span
              className="text-muted-foreground inline-flex items-center gap-1 text-sm tabular-nums"
              title="Estimated Twilio telephony cost for this pool, from call duration × list rates — for calls placed under this pool (a merged pool's own era, not the folded sources')"
            >
              <Phone className="h-3.5 w-3.5" />
              <b className="text-foreground">{formatSpend(pool.twilio_spend_micros)}</b> Telephony
            </span>
          )
        )}
      </div>
      <div className="min-h-0 flex-1">
        <PoolOutcomes pool={pool} />
      </div>
    </div>
  );
}

export type PoolTab =
  | 'overview'
  | 'availability'
  | 'events'
  | 'dispositions'
  | 'recordings'
  | 'sdrs'
  | 'numbers'
  | 'settings';

const TABS: { key: PoolTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'availability', label: 'Availability', icon: CalendarClock },
  { key: 'events', label: 'Events', icon: ScrollText },
  { key: 'dispositions', label: 'Dispositions', icon: ClipboardList },
  { key: 'recordings', label: 'Recordings', icon: Music },
  { key: 'sdrs', label: 'SDRs', icon: UserPlus },
  { key: 'numbers', label: 'Numbers', icon: PhoneCall },
  { key: 'settings', label: 'Settings', icon: Settings },
];

// Everything about one pool that isn't a live operator control. Eight views that
// were as many buttons on a card the width of a phone; the card keeps the handful of
// things pressed while dialing is happening, and the rest is one click deeper.
//
// Tab bodies mount on demand and unmount on switch: several are paged readers that
// fetch on mount, so keeping all of them alive would fire a request per tab per open.
export function PoolDetailsPanel({
  pool,
  data,
  live,
  initialTab = 'overview',
  onClose,
  onChanged,
  onSavePool,
}: {
  pool: AdminPool;
  data: BoardData;
  live: Set<number>;
  initialTab?: PoolTab;
  onClose: () => void;
  onChanged: () => void;
  onSavePool: (id: number, patch: PoolConfigPatch) => Promise<string | null>;
}) {
  const canAdmin = useHasPermission('console:admin');
  const [tab, setTab] = useState<PoolTab>(initialTab);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Read through a ref so the effect below runs ONCE per open. The board re-renders
  // on every poll, and an effect that re-ran with it would blur whatever the operator
  // is typing in, several times a minute.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      // A transcript or contact card opened FROM a tab sits above this panel and owns
      // the key while it is up — closing the panel underneath would take both.
      // Radix dismisses its own layers on a capturing listener and calls preventDefault
      // without stopping propagation, so an open popover reaches here as a handled
      // Escape. Closing the panel on it would take the whole panel with the tooltip.
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[data-overlay-above]')) return;
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, []);

  const roster = buildSdrRoster(data.directory, data.sdrs);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`${pool.name} details`}
        className="bg-card flex h-[85vh] w-[min(64rem,94vw)] flex-col overflow-hidden rounded-lg border shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="text-base font-bold">
            {pool.name} <span className="text-muted-foreground font-mono">#{pool.id}</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            title="Close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Manual activation: the default activates on arrow, which would mount and
            fetch every body the operator arrows past. */}
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as PoolTab)}
          orientation="vertical"
          activationMode="manual"
          className="min-h-0 flex-1"
        >
          <TabsList className="w-44 flex-none items-stretch gap-0.5 rounded-none border-r p-2">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} className="justify-start gap-2">
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="min-h-0 flex-1 overflow-hidden p-5">
            <TabsContent value="overview" className="h-full">
              <PoolOverviewTab pool={pool} />
            </TabsContent>
            <TabsContent value="availability" className="h-full">
              <PoolAvailabilityTab pool={pool} onChanged={onChanged} />
            </TabsContent>
            <TabsContent value="events" className="h-full">
              <PoolEventsTab pool={pool} sdrs={data.sdrs} />
            </TabsContent>
            <TabsContent value="dispositions" className="h-full">
              <PoolDispositionsTab pool={pool} />
            </TabsContent>
            <TabsContent value="recordings" className="h-full">
              <PoolRecordingsTab pool={pool} sdrs={data.sdrs} />
            </TabsContent>
            <TabsContent value="sdrs" className="h-full">
              <PoolSdrsTab pool={pool} roster={roster} live={live} onChanged={onChanged} />
            </TabsContent>
            <TabsContent value="numbers" className="h-full">
              <PoolNumbersTab pool={pool} numbers={data.numbers} />
            </TabsContent>
            <TabsContent value="settings" className="h-full">
              <PoolSettingsTab
                pool={pool}
                callerNumbers={data.numbers}
                onSave={(patch) => onSavePool(pool.id, patch)}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
